/**
 * 渠道占比饼图子组件
 * 对应板块: 板块 D — 在投广告渠道分析
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import {
  formatChartParamsDate,
  channelShowElement,
} from '@op/components/ChartCommon/formatter';
import { DBO_TM, DBO_V } from '@op/utils/format';
import { channelColorMap, chartAllColors } from '@op/constants';
import {
  channelLegendRichInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import type { TRequestPieBackType } from '@op/utils/requestUtil';
import type { TYAxisFormat } from './types';
import { dealWithOthersName } from '@op/utils/languageValue';

interface IChannelTrendPieChartProps {
  data: TRequestPieBackType;
  loading?: boolean;
  valueFormat: TYAxisFormat;
  filter?: any;
  height?: number;
}

const ChannelTrendPieChart: React.FC<IChannelTrendPieChartProps> = ({
  data,
  loading,
  valueFormat,
  filter,
  height,
}) => {
  // ==================== 数值格式化 ====================
  const valueFormatter = useMemo(() => {
    const formatters: Record<TYAxisFormat, (v: number) => string> = {
      integer: (v) => DBO_V.reviews(v),
      percent: (v) => DBO_V.percentageFormat(v),
    };
    return formatters[valueFormat];
  }, [valueFormat]);

  // ==================== 饼图数据 ====================
  const pieData = useMemo(() => {
    if (!data?.series?.length || data?.isEmpty) return [];

    // data 已经是饼图格式: { series: [{ name, value }] }
    return data.series.map((item, index) => ({
      name: item.name,
      value: item.value ?? 0,
      itemStyle: {
        color: channelColorMap[item.name] || chartAllColors[index],
      },
    }));
  }, [data]);

  // ==================== 图表配置 ====================
  const chartOption = useMemo(() => {
    if (!pieData.length) return null;

    // 计算总数
    const mentionsTotal = pieData.reduce(
      (sum, item) => sum + (item.value || 0),
      0,
    );

    // 总数为 0 时返回 null，显示空状态
    if (mentionsTotal === 0) return null;

    // 创建图例富文本样式
    const rich = channelLegendRichInfo(16, 16, false, true);
    Object.keys(rich).forEach((key) => {
      rich[key] = { ...rich[key], align: 'left', overflow: 'none' };
    });

    return {
      legend: {
        data: pieData.map((item) => item.name),
        formatter: (name: string) => {
          const realKey = dealwithChannelChartKey(name.toLocaleLowerCase());

          return `{${realKey}|} {name|${dealWithOthersName(name, 'channel')}}`;
        },
        textStyle: {
          rich: {
            name: { align: 'left', padding: [2, 0, 0, 0] },
            ...rich,
          },
        },
      },
      tooltip: {
        formatter: (params: any) => {
          // 饼图的 params 是单个对象，需要包装成数组
          const percent = DBO_V.percentage(params?.value, mentionsTotal, 2);
          return formatChartParamsDate({
            params: [params],
            listTitleFunc: (item) => item.name,
            textShowFun: (marker: string, seriesName: string) =>
              channelShowElement(marker, seriesName, undefined, true),
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: DBO_TM.timeTitle(filter),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: 'Mentions',
                },
              ];
              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
            valueFormat: (value) => {
              return `${
                isNaN(value) ? '-' : valueFormatter(value)
              }(${percent})`;
            },
          });
        },
      },
      title: [
        {
          text: `{name|${DBO_V.reviews(mentionsTotal)}}`,
          top: '43%',
          left: 'center',
          textStyle: {
            rich: {
              name: {
                fontSize: 24,
                fontFamily: 'num-m',
                color: '#000000',
                padding: [10, 0],
              },
            },
          },
        },
      ],
      series: [
        {
          type: 'pie',
          radius: ['30%', '50%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#fff',
            borderWidth: 1,
          },
          labelLine: {
            show: true,
            length: 10,
            length2: 20,
            minTurnAngle: 90,
          },
          label: {
            show: true,
            color: '#262832',
            alignTo: 'edge',
            position: 'outer',
            overflow: 'none',
            edgeDistance: 10,
            formatter: (params: any) => {
              const percent = DBO_V.percentage(params?.value, mentionsTotal, 2);
              return `{${dealwithChannelChartKey(
                params.name.toLocaleLowerCase(),
              )}|}{value| ${dealWithOthersName(
                params.name,
                'channel',
              )}}: {a|${DBO_V.reviews(params.value)}${
                !params?.value ? '' : `(${percent})`
              }}\n{hr|}\n{a|}`;
            },
            rich: {
              a: {
                lineHeight: 20,
                align: 'center',
              },
              hr: {
                borderColor: 'auto',
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              per: {
                padding: [4, 0],
              },
              ...channelLegendRichInfo(16, 16, false, true),
            },
          },
          labelLayout: (params: any) => {
            const isLeft = params.labelRect.x < (params.rect?.width || 0) / 2;
            const points = params.labelLinePoints;
            // Update the end point.
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          emphasis: {
            focus: 'self',
            label: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          blur: {
            label: { opacity: 0.2 },
            itemStyle: {
              opacity: 0.2,
            },
            labelLine: {
              lineStyle: {
                opacity: 0.2,
              },
            },
          },
          data: pieData,
        },
      ],
      xAxis: {
        show: false,
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: {
            fill: '#444444',
            fontSize: 12,
          },
        },
      ],
    };
  }, [pieData, valueFormatter, filter]);

  // ==================== 渲染 ====================
  return (
    <div className="dbo-channel-trend-chart">
      <EChartCommon
        options={chartOption || {}}
        optionsProps={{ type: 'pie' }}
        height={height}
        empty={!pieData.length || !chartOption || data?.isEmpty}
        loading={loading}
      />
    </div>
  );
};

export default React.memo(ChannelTrendPieChart);
